import React, { useEffect, useState } from "react";
import "./estatisticas.css";
import Footer from "../../components/footer/Footer";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from "recharts";

const API_BASE = process.env.REACT_APP_API_BASE;

// ==========================================================
// HELPERS DE DATA
// ==========================================================
const hojeISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const subtrairDias = (isoDate, dias) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - dias);
  return dt.toISOString().slice(0, 10);
};

// YYYY-MM-DD → DD/MM/YYYY
const formatarDataBR = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

// ==========================================================
// COMPONENTE
// ==========================================================
const Estatisticas = () => {
  // -----------------------------
  // FILTROS
  // -----------------------------
  const [tipoFiltro, setTipoFiltro] = useState("7");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");

  // -----------------------------
  // CONTROLE
  // -----------------------------
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  // -----------------------------
  // DADOS DO BACKEND
  // -----------------------------
  const [periodo, setPeriodo] = useState(null);
  const [calorias, setCalorias] = useState(null);
  const [deficit, setDeficit] = useState(null);
  const [agua, setAgua] = useState(null);
  const [peso, setPeso] = useState(null);
  const [consistencia, setConsistencia] = useState(null);
  const [resumoComportamental, setResumoComportamental] = useState(null);
  const [series, setSeries] = useState([]);

  // ==========================================================
  // DEFINIR PERÍODO
  // ==========================================================
  const calcularPeriodo = () => {
    const hoje = hojeISO();

    if (tipoFiltro === "7") {
      return { inicio: subtrairDias(hoje, 6), fim: hoje };
    }
    if (tipoFiltro === "15") {
      return { inicio: subtrairDias(hoje, 14), fim: hoje };
    }
    if (tipoFiltro === "30") {
      return { inicio: subtrairDias(hoje, 29), fim: hoje };
    }
    return { inicio: dataInicio, fim: dataFim };
  };

  // ==========================================================
  // BUSCAR ESTATÍSTICAS
  // ==========================================================
  const buscarEstatisticas = async () => {
    try {
      setLoading(true);
      setErro(null);

      const { inicio, fim } = calcularPeriodo();
      if (!inicio || !fim) {
        setLoading(false);
        return;
      }

      const res = await fetch(
        `${API_BASE}/estatisticas-dados.php?data_inicio=${inicio}&data_fim=${fim}`,
        { credentials: "include" }
      );

      const data = await res.json();

      if (data.status !== "sucesso") {
        if (data.redirect === "/login") {
          window.location.href = "/login";
          return;
        }
        setErro("Erro ao carregar estatísticas");
        setLoading(false);
        return;
      }

      // -----------------------------
      // SETAR DADOS
      // -----------------------------
      setPeriodo(data.periodo);
      setCalorias(data.calorias);
      setDeficit(data.deficit);
      setAgua(data.agua);
      setPeso(data.peso);
      setConsistencia(data.consistencia);
      setResumoComportamental(data.resumo_comportamental);

      const seriesFormatadas = (data.series_diarias || []).map((d) => ({
        ...d,
        data_formatada: formatarDataBR(d.data)
      }));

      setSeries(seriesFormatadas);

      setLoading(false);
    } catch {
      setErro("Erro inesperado ao buscar estatísticas");
      setLoading(false);
    }
  };

  // ==========================================================
  // EFEITOS
  // ==========================================================
  useEffect(() => {
    if (tipoFiltro !== "custom") {
      buscarEstatisticas();
    }
  }, [tipoFiltro]);

  useEffect(() => {
    if (tipoFiltro === "custom" && dataInicio && dataFim) {
      buscarEstatisticas();
    }
  }, [dataInicio, dataFim]);

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <div className="estatisticas-container">
      <h1>Estatísticas</h1>

      {/* FILTROS */}
      <div className="estatisticas-filtros">
        <button
          className={tipoFiltro === "7" ? "ativo" : ""}
          onClick={() => setTipoFiltro("7")}
        >
          Últimos 7 dias
        </button>

        <button
          className={tipoFiltro === "15" ? "ativo" : ""}
          onClick={() => setTipoFiltro("15")}
        >
          Últimos 15 dias
        </button>

        <button
          className={tipoFiltro === "30" ? "ativo" : ""}
          onClick={() => setTipoFiltro("30")}
        >
          Últimos 30 dias
        </button>

        <button
          className={tipoFiltro === "custom" ? "ativo" : ""}
          onClick={() => setTipoFiltro("custom")}
        >
          Personalizado
        </button>
      </div>

      {/* DATA CUSTOM */}
      {tipoFiltro === "custom" && (
        <div className="estatisticas-custom-date">
          <input
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
          />
          <span>até</span>
          <input
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
          />
        </div>
      )}

      {loading && <p>Carregando estatísticas...</p>}
      {erro && <p className="erro">{erro}</p>}

      {!loading && !erro && calorias && (
        <>
          {/* CARDS */}
          <div className="estatisticas-cards">
            <div className="card">
              <h3>Dias analisados</h3>
              <p>{periodo.total_dias}</p>
            </div>

            <div className="card">
              <h3>Média de calorias</h3>
              <p>{calorias.media_diaria} kcal/dia</p>
            </div>

            <div className="card">
              <h3>Balanço calórico</h3>
              <p className={deficit.total >= 0 ? "positivo" : "negativo"}>
                {deficit.total >= 0
                  ? `Déficit de ${deficit.total} kcal`
                  : `Superávit de ${Math.abs(deficit.total)} kcal`}
              </p>
            </div>

            <div className="card">
              <h3>Consistência</h3>
              <p>{consistencia.percentual}%</p>
            </div>
          </div>

          {/* GRÁFICOS */}
          <div className="grafico-bloco">
            <h2>Calorias</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="data_formatada" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="calorias" name="Consumidas" stroke="#ff7043" />
                <Line type="monotone" dataKey="meta_calorias" name="Meta" stroke="#4caf50" strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="grafico-bloco">
            <h2>Consumo de Água</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="data_formatada" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="agua" name="Consumido (ml)" stroke="#2196f3" />
                <Line type="monotone" dataKey="meta_agua" name="Meta (ml)" stroke="#00acc1" strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="grafico-bloco">
            <h2>Evolução do Peso</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="data_formatada" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="peso" name="Peso (kg)" stroke="#9c27b0" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      <Footer />
    </div>
  );
};

export default Estatisticas;
